import {
  respiratory,
  scale,
  temperature,
  steps,
  pressureMonitor,
  heartMonitor,
  drop,
  o2Drop,
  noFeed,
  docuForm,
  calendar,
  apple,
  sleep,
  waistLine,
  bodyFat,
  height,
  bread,
  bellDark,
  report,
  message,
  timeLogDark,
  text
} from 'assets';
import { RefRangesColors } from 'constants/colors';
import { ClinicalLogTypes } from 'constants/enums';

/**
 * Gets the SVG corresponding to the severity of the alert
 * @param {String} alertLevel the string of an int ('50', '15') corresponding to an alert's severity level
 * @param {String} alertType the type of the alert ('medical' || 'compliance'). Defaults to 'medical'.
 */
export const getSeveritySvg = (alertLevel: string, alertType: string) => {
  switch (alertLevel) {
    case '10':
      return 'warning-compliance';
    case '15':
      if (alertType === 'compliance') {
        return 'warning-compliance';
      } else {
        return 'warning-low-yellow';
      }
    case '20':
      return 'warning-yellow';
    case '30':
      return 'warning-orange';
    case '50':
      return 'warning-red';
    case '':
    default:
      return '';
  }
};

/**
 * Get SVG corresponding to a measurement class
 * @param {String} classVal the name of the rule or measurement class for which you need the icon
 * @returns {SVG} the SVG for the class
 */
export const getAlertClassSvg = (classVal: string) => {
  switch (classVal) {
    case 'steps':
      return steps;
    case 'pulse_rate':
    case 'pulse_rate_post_exercise':
    case 'NYP_Heart_Rate_Delta':
      return heartMonitor;
    case 'body_temperature':
      return temperature;
    case 'diastolic':
    case 'systolic':
    case 'pressure':
      return pressureMonitor;
    case 'fat_ratio':
      return bodyFat;
    case 'weight':
    case 'TactioCareWeightGain5LB5Days':
    case 'TactioCareWeightGain1K24Hours':
      return scale;
    case 'respiratoryRate':
    case 'fivc':
    case 'forcedExpiratoryVolume1':
    case 'forcedExpiratoryVolume1Percent':
    case 'respiratory_rate_post_exercise':
      return respiratory;
    case 'glucose_fasting':
    case 'glucose_unspecified':
    case 'TTCGlucoseFastingAvgMonthly':
    case 'TTCGlucoseUnspecifiedAvgMonthly':
      return drop;
    case 'glucose_post_meal':
    case 'glucose_post_meal_breakfast':
    case 'glucose_post_meal_lunch':
    case 'glucose_post_meal_dinner':
    case 'TTCGlucosePostMealAvgWeek':
    case 'TTCGlucosePostMealAvgMonthly':
      return apple;
    case 'glucose_pre_meal':
    case 'glucose_pre_meal_breakfast':
    case 'glucose_pre_meal_lunch':
    case 'glucose_pre_meal_dinner':
    case 'TTCGlucosePreMealAvgWeek':
    case 'TTCGlucosePreMealAvgMonthly':
      return bread;
    case 'blood_oxygen_active':
    case 'blood_oxygen':
      return o2Drop;
    case 'TactioCareNoDataLast24hours':
    case 'TactioCareNoDataLast48hours':
      return noFeed;
    case 'TactioCareMin16DaysWData':
      return calendar;
    case 'cumulativeSleep':
    case 'totalSleep':
    case 'glucose_bedtime':
    case 'glucose_midnight':
    case 'TTCGlucoseBedtimeAvgMonthly':
      return sleep;
    case 'waist':
      return waistLine;
    case 'height':
      return height;
    case 'rule_compass_dec_hr_eval':
    case 'rule_compass_inc_hr_eval':
    case 'rule_compass_inc_bp_eval':
    case 'rule_compass_dec_bp_eval':
      return docuForm;
    default:
      return noFeed;
  }
};

/**
 * Get the css color to give to a measurement depending on which zone of the reference range it is in
 * @param {String} zone the reference range zone
 * @returns {String} the css color
 */
export const getRangeColor = (zone: string | null) => {
  switch (zone) {
    case 'categoryRed':
    case 'categoryFireRed':
      return 'red-50';
    case 'categoryGreen':
    case 'categoryNormalGreen':
      return 'green-100';
    case 'categoryYellow':
      return 'yellow-50';
    case 'categoryOrange':
      return 'orange-50';
    case 'categoryBestGreen':
      return 'green-50';
    default:
      return 'blue-light';
  }
};

export const getRangeColorRefRanges = (category: string) => {
  switch (category) {
    case 'categoryRed':
      return 'bg-categoryRed50';
    case 'categoryFireRed':
      return 'bg-categoryRed';
    case 'categoryGreen':
      return 'bg-categoryGreen';
    case 'categoryYellow':
      return 'bg-categoryYellow';
    case 'categoryOrange':
      return 'bg-categoryOrange';
    case 'categoryBestGreen':
      return 'bg-categoryBestGreen';
    default:
      return '';
  }
};

export const getRangeColorRefRangesByLabel = (label: string, asClassName = true) => {
  switch (label) {
    case 'criticalLowKey':
      return asClassName ? 'bg-categoryRed50' : RefRangesColors['criticalLowKey'];
    case 'lowKey':
      return asClassName ? 'bg-categoryYellow' : RefRangesColors['lowKey'];
    case 'significantlyLowKey':
      return asClassName ? 'bg-categoryRed' : RefRangesColors['significantlyLowKey'];
    case 'normalKey':
      return asClassName ? 'bg-categoryGreen' : RefRangesColors['normalKey'];
    case 'normalHighKey':
      return asClassName ? 'bg-categoryBestGreen' : RefRangesColors['normalHighKey'];
    case 'highKey':
      return asClassName ? 'bg-categoryYellow' : RefRangesColors['highKey'];
    case 'significantlyHighKey':
      return asClassName ? 'bg-categoryOrange' : RefRangesColors['significantlyHighKey'];
    case 'criticalHighKey':
      return asClassName ? 'bg-categoryRed50' : RefRangesColors['criticalHighKey'];
  }
};

/**
 * Get SVG corresponding to a clinicalLog type
 * @param {String} type the ClinicalLog type
 * @returns {SVG} the SVG for the clinicalLog
 */
export const getClinicalLogSvg = (type: any) => {
  switch (type) {
    case ClinicalLogTypes.ALERTS_CLOSED:
      return bellDark;
    case ClinicalLogTypes.TIME_LOG:
      return timeLogDark;
    case ClinicalLogTypes.NOTE:
      return text;
    case ClinicalLogTypes.MESSAGE:
      return message;
    case ClinicalLogTypes.CARE_PLAN:
      return report;
    case ClinicalLogTypes.REPORT:
      return report; // TODO: create new icon for report
  }
  return type;
};
